import type { Audience, Place, Start } from "../../shared/goals/audience";
import { post, request } from "./api";

/** One option from Meta's own list: a place, or a starting point, with where it sits. */
export type SearchHit = { place: Place; size: number | null; path: string[] } | { start: Start; path: string[] };
export type Dimension = "geo" | "interest" | "behavior" | "income" | "workPosition" | "workEmployer" | "workIndustry";
export interface Reach {
  lower: number | null;
  upper: number | null;
  ready: boolean;
}
export interface AudienceCheck {
  ok: boolean;
  message: string | null;
  at: string;
  /** Meta is still making an audience this one uses; nothing is wrong. */
  waiting?: boolean;
}

const goal = (id: string) => `/goals/${encodeURIComponent(id)}/audience`;
const qs = (o: Record<string, string | undefined>) => new URLSearchParams(Object.entries(o).filter((e): e is [string, string] => !!e[1])).toString();

/** Who sees a goal's ads, against Meta: its list, its reach estimate, and its check (workers/src/routes/audience.ts). */
export const audienceApi = {
  search: (id: string, q: string, dimension: Dimension, country?: string) => request<{ results: SearchHit[] }>(`${goal(id)}/search?${qs({ q, dimension, country, geoType: dimension === "geo" ? "all" : undefined })}`),
  reach: (id: string, audience: Audience) => post<{ reach: Reach | null }>(`${goal(id)}/reach`, { audience }),
  check: (id: string, fresh = false) => post<{ check: AudienceCheck }>(`${goal(id)}/check${fresh ? "?fresh=1" : ""}`),
  /** The audiences the owner made in the product's Meta ad account, by Meta's id. */
  custom: (id: string) => request<{ audiences: { id: string; name: string; size: number | null }[] | null; error: string | null }>(`${goal(id)}/custom`),
  /** The events the product's pixel sent in the last 180 days, by Meta's name. */
  events: (id: string) => request<{ events: { name: string; count: number }[] | null; error: string | null }>(`${goal(id)}/events`),
};

/** What the product's Meta dataset got, and the goal's page check (workers/src/goals/tracking.ts). */
export interface PixelStep {
  found: boolean;
  other: string | null;
  tagManager: boolean;
  pixelName: string | null;
  error: string | null;
  at: string;
}
export interface TestStep {
  since: string;
  event: string | null;
  visitsOnly: boolean;
  at: string;
}
export interface Tracking {
  events: { name: string; count: number }[] | null;
  error: string | null;
  check: { page: string; pixel: PixelStep | null; test: TestStep | null } | null;
  counts: string | null;
  learnsFrom: string;
}

const goalBase = (id: string) => `/goals/${encodeURIComponent(id)}`;
export const trackingApi = {
  get: (id: string) => request<Tracking>(`${goalBase(id)}/tracking`),
  checkPage: (id: string) => post<{ pixel: PixelStep }>(`${goalBase(id)}/tracking/page`),
  checkTest: (id: string, since: string) => post<{ test: TestStep }>(`${goalBase(id)}/tracking/test`, { since }),
};
